import {
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';

import MapView, { map } from '../map/core/MapView';
import MapSelectedDevice from '../map/main/MapSelectedDevice';
import MapAccuracy from '../map/main/MapAccuracy';
import MapGeofence from '../map/MapGeofence';
import MapCurrentLocation from '../map/MapCurrentLocation';
import PoiMap from '../map/main/PoiMap';
import MapPadding from '../map/MapPadding';
import { devicesActions } from '../store';
import MapDefaultCamera from '../map/main/MapDefaultCamera';
import MapLiveRoutes from '../map/main/MapLiveRoutes';
import MapPositions from '../map/MapPositions';
import MapOverlay from '../map/overlay/MapOverlay';
import MapGeocoder from '../map/geocoder/MapGeocoder';
import MapScale from '../map/MapScale';
import MapNotification from '../map/notification/MapNotification';
import useFeatures from '../common/util/useFeatures';

const DEFAULT_FOCUS_ZOOM = 20;
const DEFAULT_FLY_DURATION = 900;

const MAP_PADDING = {
  mobile: {
    closed: { top: 12, right: 12, bottom: 96, left: 12 },
    devicesOpen: { top: 12, right: 12, bottom: 96, left: 132 },
    selectionOpen: { top: 12, right: 12, bottom: 116, left: 12 },
    fullOpen: { top: 12, right: 12, bottom: 300, left: 12 },
  },
  tablet: {
    closed: { top: 16, right: 16, bottom: 120, left: 16 },
    devicesOpen: { top: 16, right: 16, bottom: 120, left: 220 },
    selectionOpen: { top: 16, right: 16, bottom: 126, left: 16 },
    fullOpen: { top: 16, right: 16, bottom: 320, left: 16 },
  },
  desktop: {
    closed: { top: 0, right: 0, bottom: 0, left: 0 },
    devicesOpen: { top: 0, right: 0, bottom: 0, left: 0 },
    selectionOpen: { top: 0, right: 0, bottom: 0, left: 0 },
    fullOpen: { top: 0, right: 0, bottom: 0, left: 0 },
  },
};

const MainMap = forwardRef(({
  filteredPositions = [],
  selectedPosition = null,
  onEventsClick,
  devicesOpen = false,
  hasSelection = false,
  mobileCardExpanded = false,
  onSelectDevice,
}, ref) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const eventsAvailable = useSelector((state) => !!state.events.items.length);
  const positionsByDeviceId = useSelector((state) => state.session.positions);
  const features = useFeatures();

  const deviceIds = useMemo(
    () => filteredPositions
      .map((position) => position?.deviceId)
      .filter(Boolean),
    [filteredPositions],
  );

  const getDynamicPadding = useCallback(() => {
    const mode = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile';

    if (devicesOpen && hasSelection) {
      return mobileCardExpanded
        ? MAP_PADDING[mode].fullOpen
        : MAP_PADDING[mode].selectionOpen;
    }

    if (devicesOpen) {
      return MAP_PADDING[mode].devicesOpen;
    }

    if (hasSelection) {
      return mobileCardExpanded
        ? MAP_PADDING[mode].fullOpen
        : MAP_PADDING[mode].selectionOpen;
    }

    return MAP_PADDING[mode].closed;
  }, [isDesktop, isTablet, devicesOpen, hasSelection, mobileCardExpanded]);

  const focusPosition = useCallback((latitude, longitude, zoom = DEFAULT_FOCUS_ZOOM) => {
    if (!map) return;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    map.flyTo({
      center: [longitude, latitude],
      zoom,
      padding: getDynamicPadding(),
      duration: DEFAULT_FLY_DURATION,
      essential: true,
    });
  }, [getDynamicPadding]);

  useImperativeHandle(ref, () => ({
    focusPosition,
  }), [focusPosition]);

  const onMarkerClick = useCallback((positionId, deviceId) => {
    void positionId;

    if (!deviceId) return;

    if (onSelectDevice) {
      onSelectDevice(deviceId, { source: 'map' });
    } else {
      dispatch(devicesActions.selectId(deviceId));
    }

    const targetPosition = positionsByDeviceId?.[deviceId];

    if (
      Number.isFinite(targetPosition?.latitude)
      && Number.isFinite(targetPosition?.longitude)
    ) {
      focusPosition(
        targetPosition.latitude,
        targetPosition.longitude,
        DEFAULT_FOCUS_ZOOM,
      );
    }
  }, [dispatch, onSelectDevice, positionsByDeviceId, focusPosition]);

  const currentPadding = getDynamicPadding();

  return (
    <>
      <MapView>
        <MapOverlay />
        <MapGeofence />

        <MapAccuracy positions={filteredPositions} />
        <MapLiveRoutes deviceIds={deviceIds} />

        <MapPositions
          positions={filteredPositions}
          onMarkerClick={onMarkerClick}
          selectedPosition={selectedPosition}
          showStatus
        />

        <MapDefaultCamera />
        <MapSelectedDevice />
        <PoiMap />
      </MapView>

      <MapScale />
      <MapCurrentLocation />
      <MapGeocoder />

      {!features.disableEvents && (
        <MapNotification
          enabled={eventsAvailable}
          onClick={onEventsClick}
        />
      )}

      <MapPadding start={currentPadding.left} />
      <MapPadding end={currentPadding.right} />
      <MapPadding top={currentPadding.top} />
      <MapPadding bottom={currentPadding.bottom} />
    </>
  );
});

export default MainMap;